"use client";

import { Briefcase, User } from "lucide-react";
import Link from "next/link";

import { appendQuery } from "@/app/(hire)/lib/return-to";
import {
  AUTH_AUDIENCES,
  type AuthAudience,
} from "@/lib/constants/auth-audiences";
import { authTestIds } from "@/lib/data-test-ids/components.data-test-ids";
import routes from "@/lib/routes";
import { cn } from "@/lib/utils";

const ITEM_CLASSES =
  "inline-flex h-9 items-center gap-2 whitespace-nowrap rounded-full px-4 text-sm font-medium";

const AUDIENCE_ITEMS = [
  {
    audience: AUTH_AUDIENCES.JOB_SEEKER,
    label: "Job seekers",
    Icon: User,
    href: routes.getSignIn(),
    testId: authTestIds.AUDIENCE_TOGGLE_JOB_SEEKERS,
  },
  {
    audience: AUTH_AUDIENCES.DISTRICT_STAFF,
    label: "District staff",
    Icon: Briefcase,
    href: routes.getHireSignIn(),
    testId: authTestIds.AUDIENCE_TOGGLE_DISTRICT_STAFF,
  },
] as const;

interface AuthAudienceToggleProps {
  active: AuthAudience;
  query: string;
}

export function AuthAudienceToggle({ active, query }: AuthAudienceToggleProps) {
  return (
    <nav
      aria-label="Who are you signing in as?"
      className="mx-auto mb-4 flex w-fit items-center rounded-full border border-line-faint p-1"
      data-testid={authTestIds.AUDIENCE_TOGGLE}
    >
      {AUDIENCE_ITEMS.map(({ audience, label, Icon, href, testId }) =>
        audience === active ? (
          <span
            key={audience}
            aria-current="page"
            className={cn(ITEM_CLASSES, "bg-primary text-primary-foreground")}
            data-testid={testId}
          >
            <Icon className="size-4 shrink-0" aria-hidden="true" />
            {label}
          </span>
        ) : (
          <Link
            key={audience}
            href={appendQuery(href, query)}
            className={cn(
              ITEM_CLASSES,
              "text-ink-subtle transition-colors hover:bg-muted hover:text-foreground",
            )}
            data-testid={testId}
          >
            <Icon className="size-4 shrink-0" aria-hidden="true" />
            {label}
          </Link>
        ),
      )}
    </nav>
  );
}
